/**
 * Public availability calendar shown on /trainers/:id (member side).
 * Renders 4 weeks of slots; member taps an available slot → compact booking
 * card → Razorpay payment via /api/pro/bookings/instant.
 */
import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, Lock } from "lucide-react";
import { apiClient } from "../../services/api";
import { useAuthStore } from "../../store/authStore";
import { toast } from "sonner";

function loadRazorpay() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    if (!document.getElementById("razorpay-script")) {
      const s = document.createElement("script");
      s.id = "razorpay-script";
      s.src = "https://checkout.razorpay.com/v1/checkout.js";
      document.body.appendChild(s);
    }
    const wait = (n = 0) => {
      if (window.Razorpay) return resolve(true);
      if (n > 40) return resolve(false);
      setTimeout(() => wait(n + 1), 100);
    };
    wait();
  });
}

export default function BookingCalendar({ trainerUserId, trainerName }) {
  const user = useAuthStore((s) => s.user);
  const [data, setData] = useState(null);
  const [weekIdx, setWeekIdx] = useState(0);
  const [dayIdx, setDayIdx] = useState(0); // mobile: shows dayIdx..dayIdx+2
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    apiClient.get(`/pro/availability/${trainerUserId}`).then((r) => setData(r.data)).catch(() => setData(null));
  }, [trainerUserId]);

  if (!data) return <div data-testid="booking-calendar" className="text-ink-soft text-[13px] py-4">Loading availability…</div>;
  if (!data.weeks || data.weeks.length === 0) {
    return <div data-testid="booking-calendar" className="text-ink-soft text-[13px] py-4">This trainer hasn't published self-serve slots yet. Use Send Enquiry to schedule.</div>;
  }

  const week = data.weeks[weekIdx];
  const visibleDays = week.days.slice(dayIdx, dayIdx + 3);

  const book = async () => {
    if (!user) { toast("Please log in to book"); return; }
    if (!selected) return;
    setBusy(true);
    try {
      const ok = await loadRazorpay();
      if (!ok) throw new Error("Razorpay failed to load");
      const { data: order } = await apiClient.post("/pro/bookings/instant", {
        trainer_user_id: trainerUserId,
        session_at: selected.session_at,
        method: "jitsi",
      });
      const opts = {
        key: order.key_id,
        amount: order.amount,
        currency: order.currency,
        name: "FitFrik",
        description: `Session with ${trainerName}`,
        order_id: order.order_id,
        prefill: { name: order.name || "", email: order.email || "" },
        theme: { color: "#00D4FF" },
        handler: async (resp) => {
          try {
            await apiClient.post("/pro/bookings/instant/verify", {
              intent_id: order.intent_id,
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            toast.success("Payment received", {
              description: `Waiting for ${trainerName} to confirm your session.`,
            });
            setSelected(null);
            // Refresh availability to remove the booked slot
            apiClient.get(`/pro/calendar/${trainerUserId}`).then((r) => setData(r.data));
          } catch (e) {
            toast.error("Verification failed");
          }
        },
        modal: { ondismiss: () => setBusy(false) },
      };
      const rzp = new window.Razorpay(opts);
      rzp.on("payment.failed", () => toast.error("Payment failed"));
      rzp.open();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Could not start booking");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="booking-calendar">
      <div className="flex items-center justify-between mb-3">
        <button onClick={() => setWeekIdx(Math.max(0, weekIdx - 1))} disabled={weekIdx === 0} aria-label="Previous week" data-testid="cal-prev" className="w-10 h-10 grid place-items-center disabled:opacity-30 rounded-full hover:bg-canvas2"><ChevronLeft size={18} /></button>
        <div className="text-[13px] font-semibold">Week of {week.week_start}</div>
        <button onClick={() => setWeekIdx(Math.min(3, weekIdx + 1))} disabled={weekIdx === 3} aria-label="Next week" data-testid="cal-next" className="w-10 h-10 grid place-items-center disabled:opacity-30 rounded-full hover:bg-canvas2"><ChevronRight size={18} /></button>
      </div>

      <div className="flex gap-2 mb-3">
        <button onClick={() => setDayIdx(Math.max(0, dayIdx - 1))} disabled={dayIdx === 0} aria-label="Earlier days" className="w-9 h-9 rounded-full grid place-items-center bg-canvas2 disabled:opacity-30"><ChevronLeft size={14} /></button>
        <div className="grid grid-cols-3 gap-2 flex-1">
          {visibleDays.map((d) => (
            <div key={d.date} className="text-center">
              <div className="text-[10px] uppercase tracking-wider font-bold text-ink-tertiary">{d.day}</div>
              <div className="text-[15px] font-extrabold font-barlow-cond">{d.date.slice(8, 10)}</div>
            </div>
          ))}
        </div>
        <button onClick={() => setDayIdx(Math.min(week.days.length - 3, dayIdx + 1))} disabled={dayIdx >= week.days.length - 3} aria-label="Later days" className="w-9 h-9 rounded-full grid place-items-center bg-canvas2 disabled:opacity-30"><ChevronRight size={14} /></button>
      </div>

      <div className="grid grid-cols-3 gap-2" data-testid="slots-grid">
        {visibleDays.map((d) => (
          <div key={d.date} className="space-y-1.5">
            {d.slots.length === 0 && <div className="text-[11px] text-ink-tertiary text-center py-2">—</div>}
            {d.slots.map((s) => {
              const sel = selected?.session_at === s.session_at;
              if (!s.available) {
                return (
                  <div key={s.slot_id} className="flex items-center justify-center text-[11px] text-ink-tertiary h-11 rounded-[6px]" style={{ background: "rgba(140,135,128,0.10)" }} data-testid={`slot-locked-${s.slot_id}`}>
                    <Lock size={11} />
                  </div>
                );
              }
              return (
                <button
                  key={s.slot_id}
                  onClick={() => setSelected(s)}
                  data-testid={`slot-${s.slot_id}`}
                  className="w-full h-11 rounded-[6px] text-[12px] font-semibold"
                  style={{
                    background: sel ? "#1A1A1A" : "rgba(42,40,37,0.08)",
                    color: sel ? "#fff" : "#1A1A1A",
                  }}
                >
                  {s.time}
                </button>
              );
            })}
          </div>
        ))}
      </div>

      {selected && (
        <div className="fixed left-0 right-0 bottom-0 z-30 px-4 pb-5" style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 16px)" }} data-testid="booking-summary">
          <div className="max-w-md mx-auto bg-white border border-line2 rounded-[18px] p-4" style={{ boxShadow: "0 12px 40px rgba(0,0,0,0.18)" }}>
            <div className="font-barlow-cond font-extrabold text-[22px] leading-tight">{new Date(selected.session_at).toLocaleString("en-IN", { weekday: "short", day: "numeric", month: "short", hour: "numeric", minute: "2-digit" })}</div>
            <div className="text-[13px] text-ink-soft">{data.duration_minutes} min · Online (Jitsi)</div>
            <div className="flex items-center justify-between mt-3 gap-2">
              <div className="font-barlow-cond font-extrabold text-[24px]">₹{data.rate_inr.toLocaleString("en-IN")}</div>
              <div className="flex gap-2">
                <button onClick={() => setSelected(null)} aria-label="Close" className="h-11 px-4 rounded-pill border border-line2 font-semibold text-[13px]" style={{ borderRadius: 100 }}>Cancel</button>
                <button onClick={book} disabled={busy} data-testid="book-now" className="h-11 px-5 rounded-pill font-bold disabled:opacity-50" style={{ background: "#00D4FF", color: "#000", borderRadius: 100 }}>{busy ? "Loading…" : "Book Now"}</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
